import { Box, Container, Grid, Typography } from "@mui/material";
import { spacing } from "~/pages/reports/utils/reportsConstants";
import { Stats } from "~/components/DetailsPage/components";
import { ReportsPrintScoreBlock } from "~/pages/reports/components";
import {
  CvssRiskContent,
  CvssRiskProvider,
  CvssVector,
} from "~/components/vulnerabilities";
import { AssetReportRiskFactors } from "~/pages/reports/AssetReport/components/AssetReportRiskFactors";
import { Markdown } from "~/components/markdown";
import { CheckReportExposedAssets } from "./components/CheckReportExposedAssets";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { CheckQueriesPropertyItems } from "~/pages/space/security/components/Check/CheckQueries/components/CheckQueriesPropertyItems";
import { CheckQueriesMql } from "~/pages/space/security/components/Check/CheckQueries/components/CheckQueriesMql";
import { Text } from "~/components/ui-library";
import { ReportsSectionHeading } from "~/pages/reports/components/ReportsSectionHeading";
import { useCheckReport } from "~/pages/reports/CheckReport/hooks/useCheckReport";

export const CheckReport = () => {
  const {
    title,
    description,
    hasRemediations,
    properties,
    mql,
    riskValue,
    rating,
    cvss,
    stats,
    failedAssets,
    riskFactorsWithDocs,
    mappings,
    remediations,
    isLoading,
    hasError,
  } = useCheckReport();

  if (isLoading) {
    return <LoadingPage what="Check Report" />;
  }

  if (hasError) {
    return <LoadingFailedPage what="Check Report" />;
  }

  return (
    <Container sx={{ p: 4 }}>
      <Grid container>
        <Grid size={12} sx={{ mb: spacing[50] }}>
          <Typography variant="h4" fontWeight={700}>
            {title}
          </Typography>
        </Grid>
        <Grid size={12}>
          <ReportsSectionHeading heading="Summary" disableUpperPadding />
        </Grid>

        <Stats id="detail-content-stats" stats={stats} />
        <Box sx={{ position: "relative", width: "100%" }}>
          {riskValue != null && rating && (
            <Box
              sx={{
                float: "right",
                ml: 3,
                mb: 1,
                mt: 3.75,
              }}
            >
              <ReportsPrintScoreBlock
                mappings={mappings}
                ratingValues={{
                  value: riskValue,
                  rating: rating,
                }}
              />
            </Box>
          )}
          {description && (
            <Markdown
              source={description}
              externalLinksInNew
              copyButton={false}
              sx={{
                my: spacing[25],
                "& p": {
                  fontSize: 14,
                  fontFamily: "Roboto",
                  lineHeight: "1.43",
                  textAlign: "justify",
                },
              }}
            />
          )}
        </Box>

        <Grid size={12} sx={{ mb: spacing[50] }}>
          <AssetReportRiskFactors
            dataType="check"
            riskFactors={riskFactorsWithDocs}
          />
        </Grid>

        {cvss?.value && (
          <Grid size={12} sx={{ mb: spacing[50] }}>
            <CvssRiskProvider vector={cvss.vector} isPrintView>
              <ReportsSectionHeading heading="CVSS v3 score" sx={{ pb: 0 }} />
              <Box sx={{ pt: 1, pb: spacing[25] }}>
                <CvssVector />
              </Box>
              <CvssRiskContent />
            </CvssRiskProvider>
          </Grid>
        )}

        {hasRemediations && (
          <Grid size={12}>
            <ReportsSectionHeading heading="Remediation" />
            <Box>
              {remediations.entries.map((remediation) => (
                <Markdown
                  key={remediation.id}
                  source={remediation.desc}
                  externalLinksInNew
                  copyButton={false}
                />
              ))}
            </Box>
          </Grid>
        )}

        <Grid size={12}>
          <ReportsSectionHeading heading="Queries" />
          {(properties?.length || 0) > 0 && (
            <>
              <Text bold>Properties</Text>
              <CheckQueriesPropertyItems properties={properties} />
            </>
          )}
          {mql && (
            <>
              <Text bold>MQL</Text>
              <CheckQueriesMql mql={mql} />
            </>
          )}
        </Grid>

        {failedAssets.length > 0 && (
          <Grid size={12}>
            <CheckReportExposedAssets
              assets={failedAssets}
              heading="Exposed assets"
            />
          </Grid>
        )}
      </Grid>
    </Container>
  );
};
